import type { FeedSignal, VerificationStatus } from "../types";

/**
 * Mock detail content for the signal modal (Phase D, E5 + coordination data).
 * These panels (evidence, activation context, related contacts, recommended
 * asset) are not on v_signal_feed; at live time they come from the signal
 * record + firm relationship service + Attention Object library. In Phase D
 * they are deterministically generated from the signal so every panel renders
 * with stable content. No Math.random.
 */

export interface RelatedContact {
  id: string;
  name: string;
  title: string;
  strength: "strong" | "moderate" | "weak";
  /** Where the contact came from (Web Summit 2025, LinkedIn, ...). */
  source: string;
  /** Owner display name ("You" when the viewer owns the contact). */
  owner: string;
  /** Whether the viewer owns this contact (mine vs another, respecting visibility). */
  ownedByViewer: boolean;
  /** Why this contact is tied to the signal. */
  matchBasis: string;
}

export interface EvidenceRecord {
  sourceUrl: string | null;
  sourceLabel: string;
  retrievedAt: string | null;
  publishedAt: string | null;
  status: VerificationStatus;
  deadlineAt: string | null;
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

const DAY = 86_400_000;

export function evidenceFor(signal: FeedSignal): EvidenceRecord {
  const timeSensitive = /Tariff|Regulatory|Litigation|Covenant/i.test(
    signal.eventType ?? "",
  );
  const deadlineAt =
    signal.deadlineAt ??
    (timeSensitive
      ? new Date(Date.now() + (7 + (hash(signal.id) % 21)) * DAY).toISOString()
      : null);
  return {
    sourceUrl: signal.url,
    sourceLabel: signal.source,
    retrievedAt: signal.lastObservedAt,
    publishedAt: signal.firstObservedAt,
    status: signal.verification,
    deadlineAt,
  };
}

// Activation context is now the honest get_signal_detail v13 projection
// (src/features/signals/activation.ts + the live adapter). The hash-selected mock
// (ACTIVATION_VARIANTS / activationContextFor) was retired in T1 — it rendered
// the same fabricated variant on every signal.

const CONTACT_NAMES = [
  "Alex Chen",
  "Jordan Ellis",
  "Sam Rivera",
  "Taylor Brooks",
  "Morgan Lee",
  "Casey Flynn",
  "Riley Novak",
  "Devon Park",
];
const CONTACT_TITLES = [
  "Chief Financial Officer",
  "General Counsel",
  "VP Finance",
  "Head of Strategy",
  "Chief Restructuring Officer",
  "Treasurer",
];
const STRENGTHS: RelatedContact["strength"][] = ["strong", "moderate", "weak"];
const CONTACT_SOURCES = [
  "Web Summit 2025",
  "LinkedIn",
  "Referral intro",
  "Conference 2026",
];
// "You" marks a viewer-owned contact; the rest are teammates (another owner).
const CONTACT_OWNERS = ["You", "Dara Okafor", "Marco Reyes", "Priya Nair"];
const MATCH_BASES = [
  "Same account as this signal",
  "Named in the source article",
  "Prior engagement contact",
  "Colleague relationship match",
];

export function relatedContactsFor(signal: FeedSignal): RelatedContact[] {
  const h = hash(signal.id);
  const count = 2 + (h % 2); // 2 or 3
  return Array.from({ length: count }).map((_, i) => {
    const seed = h + i * 2654435761;
    const owner = CONTACT_OWNERS[(seed >>> 9) % CONTACT_OWNERS.length]!;
    return {
      id: `${signal.id}-c${i}`,
      name: CONTACT_NAMES[seed % CONTACT_NAMES.length]!,
      title: CONTACT_TITLES[(seed >>> 3) % CONTACT_TITLES.length]!,
      strength: STRENGTHS[(seed >>> 6) % STRENGTHS.length]!,
      source: CONTACT_SOURCES[(seed >>> 12) % CONTACT_SOURCES.length]!,
      owner,
      ownedByViewer: owner === "You",
      matchBasis: MATCH_BASES[(seed >>> 15) % MATCH_BASES.length]!,
    };
  });
}

// Recommended-asset matching was removed in the parity amendment (Item 1): no
// asset data source exists yet, so the modal renders an honest empty state
// rather than a fabricated match. The Attention Object Library will supply real
// matches in a later slice.
